// ═══════════════════════════════════════════════════════════════════
// dbHelpers — Helpers para escrituras seguras a Firestore
// ═══════════════════════════════════════════════════════════════════
// Reglas:
//   1. Toda escritura pasa por estos helpers
//   2. Nunca falta empresaId en colecciones de Capa 2
//   3. Nunca faltan timestamps
//   4. Nunca un payload incluye campos peligrosos (rol, tipo, admin)
//   5. Todo queda listo para export a BigQuery
// ═══════════════════════════════════════════════════════════════════

import { serverTimestamp } from 'firebase/firestore'

/**
 * Agrega timestamps estándar a un payload.
 * - En documentos nuevos (isNew=true): agrega creadoEn + actualizadoEn
 * - En updates (isNew=false): solo agrega actualizadoEn
 *
 * @param {object} data - Payload original
 * @param {boolean} [isNew=true] - Si es un documento nuevo
 * @returns {object} Payload con timestamps
 */
export function withTimestamps(data, isNew = true) {
  const ts = { actualizadoEn: serverTimestamp() }
  if (isNew) ts.creadoEn = serverTimestamp()
  return { ...data, ...ts }
}

/**
 * Garantiza que empresaId esté presente en el payload.
 * Lanza error si no se proporciona (fail-fast para detectar bugs).
 *
 * @param {object} data - Payload original
 * @param {string} empresaId - ID de la empresa
 * @returns {object} Payload con empresaId
 */
export function withEmpresaId(data, empresaId) {
  if (!empresaId) {
    throw new Error('[dbHelpers] empresaId requerido — no se puede escribir sin tenant')
  }
  return { ...data, empresaId }
}

/**
 * Combina timestamps + empresaId para documentos nuevos de Capa 2.
 * Es el helper principal para addDoc en colecciones que pertenecen a una empresa.
 *
 * @param {object} data - Payload original
 * @param {string} empresaId - ID de la empresa
 * @returns {object} Payload listo para addDoc
 */
export function forCreate(data, empresaId) {
  return withTimestamps(withEmpresaId(data, empresaId), true)
}

/**
 * Prepara un payload para updateDoc en colecciones de Capa 2.
 * Agrega actualizadoEn. NO agrega empresaId al update (no se debe cambiar).
 *
 * @param {object} data - Campos a actualizar
 * @returns {object} Payload listo para updateDoc
 */
export function forUpdate(data) {
  return withTimestamps(data, false)
}

/**
 * Prepara un payload para addDoc en colecciones de plataforma (Capa 0/1).
 * Solo agrega timestamps, sin empresaId (no pertenece a ninguna empresa).
 *
 * @param {object} data - Payload original
 * @returns {object} Payload listo para addDoc
 */
export function forPlatformCreate(data) {
  return withTimestamps(data, true)
}

// ── Sanitización ──────────────────────────────────────────────────

/**
 * Campos que NUNCA debe poder mandar un usuario desde el frontend.
 * Si alguno de estos campos aparece en un payload, se elimina silenciosamente.
 * Previene escalación de privilegios.
 */
const FORBIDDEN_FIELDS = new Set([
  'rol',
  'rolId',
  'tipo',
  'esAdmin',
  'admin',
  'superAdmin',
  'permisos',
  'puedeEmitirDifusiones',
  '_deleted',
  '__proto__',
  'constructor',
  'prototype',
])

/**
 * Elimina campos peligrosos de un payload.
 * Previene que un usuario se auto-promueva inyectando campos como 'rol' o 'tipo'.
 *
 * @param {object} data - Payload del frontend
 * @param {string[]} [allowFields] - Override: campos normalmente prohibidos que SÍ se permiten
 *                                    (para funciones admin que legítimamente cambian roles)
 * @returns {object} Payload limpio
 */
export function sanitize(data, allowFields = []) {
  const allowSet = new Set(allowFields)
  const clean = {}
  for (const [key, value] of Object.entries(data)) {
    if (FORBIDDEN_FIELDS.has(key) && !allowSet.has(key)) continue
    // Prevenir prototype pollution
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') continue
    // No permitir valores undefined (Firestore los rechaza)
    if (value === undefined) continue
    clean[key] = value
  }
  return clean
}

/**
 * Pipeline completo para crear un documento de Capa 2 con sanitización.
 * sanitize → withEmpresaId → withTimestamps
 *
 * @param {object} data - Payload crudo del frontend
 * @param {string} empresaId - ID de la empresa
 * @param {string[]} [allowFields] - Campos normalmente prohibidos que se permiten
 * @returns {object} Payload seguro y completo
 */
export function safeCreate(data, empresaId, allowFields = []) {
  return forCreate(sanitize(data, allowFields), empresaId)
}

/**
 * Pipeline completo para actualizar un documento de Capa 2 con sanitización.
 * sanitize → withTimestamps (solo actualizadoEn)
 *
 * @param {object} data - Payload crudo del frontend
 * @param {string[]} [allowFields] - Campos normalmente prohibidos que se permiten
 * @returns {object} Payload seguro para updateDoc
 */
export function safeUpdate(data, allowFields = []) {
  return forUpdate(sanitize(data, allowFields))
}

// ── Validación de tipos ───────────────────────────────────────────

/**
 * Valida que un string no esté vacío después de trim.
 * Lanza error descriptivo si no cumple.
 */
export function requireString(value, fieldName) {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`[validación] ${fieldName} es requerido y debe ser un string no vacío`)
  }
  return value.trim()
}

/**
 * Valida que un valor sea uno de los permitidos.
 */
export function requireEnum(value, allowed, fieldName) {
  const values = allowed instanceof Set ? allowed : new Set(Object.values(allowed))
  if (!values.has(value)) {
    throw new Error(`[validación] ${fieldName} debe ser uno de: ${[...values].join(', ')}`)
  }
  return value
}

/**
 * Valida que sea un array (puede estar vacío).
 */
export function requireArray(value, fieldName) {
  if (!Array.isArray(value)) {
    throw new Error(`[validación] ${fieldName} debe ser un array`)
  }
  return value
}
